import { motion, useMotionValue, useSpring } from "framer-motion";
import { Sparkle } from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { MaskLines, Eyebrow, Reveal } from "../components/Reveal";
import { PrimaryCTA } from "../components/Buttons";
import { Sculpture } from "./Sculptures";
import { EASE } from "../lib/util";

const TierCard = ({ tier, i, isFa, priceNote }) => {
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 150, damping: 18 });
  const sry = useSpring(ry, { stiffness: 150, damping: 18 });
  const move = (e) => {
    if (window.matchMedia("(pointer: coarse)").matches) return;
    const r = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 10);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 10);
  };
  return (
    <motion.article
      data-testid={`package-${tier.latin.toLowerCase()}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 1.1, ease: EASE, delay: i * 0.12 }}
      onMouseMove={move}
      onMouseLeave={() => {
        rx.set(0);
        ry.set(0);
      }}
      style={{ rotateX: srx, rotateY: sry, transformPerspective: 1100 }}
      className="group relative flex flex-col rounded-[32px] border border-noor-emerald/10 bg-gradient-to-b from-white to-noor-cream p-4 shadow-[0_30px_80px_-40px_rgba(3,52,48,0.45)] transition-shadow duration-700 hover:shadow-[0_40px_100px_-40px_rgba(3,52,48,0.65)]"
    >
      <div className="relative h-72 overflow-hidden rounded-[24px] bg-noor-emerald md:h-80">
        <div className="absolute inset-0 transition-transform duration-[1200ms] group-hover:-translate-y-1.5">
          <Sculpture kind={i} />
        </div>
        <span className="absolute start-5 top-5 font-brand text-xs text-noor-champagne" dir="ltr">0{i + 1}</span>
      </div>
      <div className="flex flex-1 flex-col px-3 pb-3 pt-7">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="font-display text-4xl text-noor-emerald">{tier.name}</h3>
          {isFa && <span className="font-brand text-xs text-noor-gold" dir="ltr">{tier.latin}</span>}
        </div>
        <span className="mt-4 inline-flex w-fit rounded-full border border-noor-emerald/15 px-3 py-1 text-xs text-noor-emerald/80">{tier.scope}</span>
        <p className="mt-5 flex-1 text-[15px] leading-relaxed text-noor-emerald/80">{tier.text}</p>
        <p className="mt-7 flex items-center gap-2 border-t border-noor-emerald/10 pt-5 text-sm font-medium text-noor-emerald" data-testid={`package-${tier.latin.toLowerCase()}-price-note`}>
          <Sparkle size={14} className="text-noor-gold" />
          {tier.availability || priceNote}
        </p>
      </div>
    </motion.article>
  );
};

export const Packages = () => {
  const { t, isFa } = useLang();
  const p = t.packages;
  return (
    <section id="packages" data-testid="packages-section" className="relative z-[2] bg-noor-pearl py-28 text-noor-emerald md:py-40">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
        <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-8">
            <Eyebrow tone="light">{p.eyebrow}</Eyebrow>
            <MaskLines lines={p.lines} className="display-lg mt-6 font-display text-noor-emerald" lineClassName="italic-accent text-noor-gold" testId="packages-headline" />
          </div>
          <Reveal as="p" className="text-[15px] leading-relaxed text-noor-emerald/80 lg:col-span-4">{p.desc}</Reveal>
        </div>
        <div className="mt-16 grid gap-6 md:grid-cols-3 md:gap-5 lg:gap-8">
          {p.tiers.map((tier, i) => (
            <TierCard key={tier.latin} tier={tier} i={i} isFa={isFa} priceNote={p.priceNote} />
          ))}
        </div>
        <Reveal className="mt-16 flex flex-col items-start justify-between gap-8 rounded-[32px] bg-noor-emerald p-8 md:flex-row md:items-center md:p-10">
          <p className="max-w-2xl text-[15px] leading-relaxed text-noor-mist" data-testid="packages-no-price-note">{p.note}</p>
          <PrimaryCTA linkKey="plan" testId="packages-cta" className="shrink-0">{p.cta}</PrimaryCTA>
        </Reveal>
      </div>
    </section>
  );
};
